import { useState, useEffect, useMemo } from 'react'
import supabase from '../lib/supabase'
import { sortMatchesNextFirst } from '../lib/matchOrdering'

// Custom hook for Supabase queries
export const useSupabaseQuery = (table, options = {}) => {
  const [data, setData] = useState(null)
  const [error, setError] = useState(null)
  const [loading, setLoading] = useState(true)
  const [refreshTrigger, setRefreshTrigger] = useState(0)

  const refresh = () => setRefreshTrigger((prev) => prev + 1)

  const optionsKey = useMemo(() => JSON.stringify(options ?? {}), [options])

  useEffect(() => {
    let cancelled = false

    const fetchData = async () => {
      setLoading(true)
      setError(null)

      try {
        let query = supabase.from(table).select()

        if (options?.eq) {
          Object.entries(options.eq).forEach(([key, value]) => {
            query = query.eq(key, value)
          })
        }

        if (options?.contains) {
          Object.entries(options.contains).forEach(([key, value]) => {
            query = query.contains(key, value)
          })
        }

        if (options?.order) {
          query = query.order(options.order.column, {
            ascending: options.order.ascending,
          })
        }

        if (options?.limit) {
          query = query.limit(options.limit)
        }

        const { data: result, error: queryError } = await query

        if (queryError) throw queryError
        if (!cancelled) setData(result)
      } catch (err) {
        if (!cancelled) {
          setError(err?.message || 'Unknown error')
          if (import.meta.env.DEV) {
            console.error('Error fetching data:', err)
          }
        }
      } finally {
        if (!cancelled) setLoading(false)
      }
    }

    fetchData()

    return () => {
      cancelled = true
    }
  }, [table, optionsKey, refreshTrigger])

  return { data, error, loading, refresh }
}

const calculateAge = (dateOfBirth) => {
  if (!dateOfBirth) return null
  const birth = new Date(`${dateOfBirth}T00:00:00`)
  if (Number.isNaN(birth.getTime())) return null

  const today = new Date()
  let age = today.getFullYear() - birth.getFullYear()
  const monthDiff = today.getMonth() - birth.getMonth()

  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
    age -= 1
  }

  return age
}

// Hook for PUBLIC team members.
// Players must be both active and published to appear in the current squad.
// Staff only use the publication flag; player lifecycle does not apply to staff.
// Public age precedence: explicit age_override -> calculated DOB age -> legacy age.
export const useTeamMembers = (isStaff = false) => {
  const result = useSupabaseQuery('team_members_aox1234', {
    eq: isStaff
      ? { is_staff: true, is_published: true }
      : { is_staff: false, is_published: true, is_active: true },
    order: { column: 'sort_order', ascending: true },
  })

  const data = useMemo(() => {
    if (!result.data) return result.data

    return result.data.map((member) => {
      const calculatedAge = calculateAge(member.date_of_birth)
      const overrideAge = Number.isInteger(member.age_override) ? member.age_override : null
      return {
        ...member,
        calculated_age: calculatedAge,
        age: overrideAge ?? calculatedAge ?? member.age,
      }
    })
  }, [result.data])

  return { ...result, data }
}

// Public match feed. Hidden Matchday Studio-only fixtures stay out of the main site.
export const useMatches = (status = null) => {
  const options = {
    eq: { is_public: true },
    order: { column: 'date', ascending: true },
  }
  if (status) options.eq.status = status
  const result = useSupabaseQuery('matches_aox1234', options)
  const data = useMemo(() => {
    if (!result.data) return result.data
    return sortMatchesNextFirst(result.data)
  }, [result.data])
  return { ...result, data }
}

// Public news feed.
// Drafts are never shown. Scheduled items become visible automatically when
// published_at is reached. Featured items are promoted before normal items.
export const useNews = (limit = null) => {
  const [data, setData] = useState(null)
  const [error, setError] = useState(null)
  const [loading, setLoading] = useState(true)
  const [refreshTrigger, setRefreshTrigger] = useState(0)

  const refresh = () => setRefreshTrigger((prev) => prev + 1)

  useEffect(() => {
    let cancelled = false

    const fetchNews = async () => {
      setLoading(true)
      setError(null)

      try {
        const { data: rows, error: queryError } = await supabase
          .from('news_aox1234')
          .select('*')
          .order('published_at', { ascending: false })

        if (queryError) throw queryError

        const now = Date.now()
        const publicRows = (rows || [])
          .filter((item) => {
            const status = item.status || 'published'
            if (status === 'draft') return false

            if (!item.published_at) return status === 'published'
            const publishTime = new Date(item.published_at).getTime()
            if (Number.isNaN(publishTime)) return status === 'published'

            return publishTime <= now
          })
          .sort((a, b) => {
            if (Boolean(a.is_featured) !== Boolean(b.is_featured)) {
              return a.is_featured ? -1 : 1
            }
            return new Date(b.published_at || 0).getTime() - new Date(a.published_at || 0).getTime()
          })

        const result = limit ? publicRows.slice(0, limit) : publicRows
        if (!cancelled) setData(result)
      } catch (err) {
        if (!cancelled) {
          setError(err?.message || 'Unknown error')
          if (import.meta.env.DEV) console.error('Error fetching news:', err)
        }
      } finally {
        if (!cancelled) setLoading(false)
      }
    }

    fetchNews()

    return () => {
      cancelled = true
    }
  }, [limit, refreshTrigger])

  return { data, error, loading, refresh }
}

export const useAcademyGroups = () =>
  useSupabaseQuery('academy_groups_aox1234', {
    order: { column: 'created_at', ascending: true },
  })

export const useTournaments = () =>
  useSupabaseQuery('tournaments_aox1234', {
    order: { column: 'created_at', ascending: false },
  })

export const useGalleryItems = (tag = null) => {
  const options = { order: { column: 'date', ascending: false } }
  if (tag) options.contains = { tags: [tag] }
  return useSupabaseQuery('gallery_items_aox1234', options)
}

export const useStandings = () =>
  useSupabaseQuery('standings_aox1234', {
    order: { column: 'position', ascending: true },
  })

export const useSponsors = () =>
  useSupabaseQuery('sponsors_aox1234', {
    order: { column: 'sort_order', ascending: true },
  })

export const useContactForms = () =>
  useSupabaseQuery('contact_forms_aox1234', {
    order: { column: 'created_at', ascending: false },
  })

export const useSiteConfig = () =>
  useSupabaseQuery('site_config_aox1234', { limit: 1 })

export const useAuth = () => {
  const [session, setSession] = useState(null)
  const [loading, setLoading] = useState(true)
  const [user, setUser] = useState(null)

  useEffect(() => {
    const getSession = async () => {
      setLoading(true)
      try {
        const { data, error } = await supabase.auth.getSession()
        if (!error && data?.session) {
          setSession(data.session)
          setUser(data.session.user)
        }
      } catch (error) {
        if (import.meta.env.DEV) console.error('Session error:', error)
      } finally {
        setLoading(false)
      }
    }

    getSession()

    const { data: authListener } = supabase.auth.onAuthStateChange(
      async (event, currentSession) => {
        setSession(currentSession)
        setUser(currentSession?.user ?? null)
        setLoading(false)
      }
    )

    return () => {
      if (authListener && authListener.subscription) {
        authListener.subscription.unsubscribe()
      }
    }
  }, [])

  const login = async (email, password) => {
    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      })

      if (error) throw error

      if (data?.user) {
        setUser(data.user)
        setSession(data.session)
      }

      return { data, error: null }
    } catch (error) {
      if (import.meta.env.DEV) console.error('Login error:', error.message)
      return { data: null, error }
    }
  }

  const logout = async () => {
    try {
      const { error } = await supabase.auth.signOut()
      if (error) throw error
      setUser(null)
      setSession(null)
    } catch (error) {
      if (import.meta.env.DEV) console.error('Logout error:', error.message)
    }
  }

  return { session, user, loading, login, logout }
}
